import { apiEndpoints } from './supabase'
import type {
  Project,
  PressKitItem,
  Career,
  BlogPost,
  CreateProject,
  UpdateProject,
  CreatePressKitItem,
  UpdatePressKitItem,
  CreateCareer,
  UpdateCareer,
  CreateBlogPost,
  UpdateBlogPost,
  ApiResponse,
  ApiListResponse
} from './types'

// Generic API function
async function apiCall<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    // Check if we're using mock endpoints (no Supabase configured)
    if (url.startsWith('/api/mock/')) {
      // Return mock data for build-time compatibility
      return { data: [] as T }
    }

    const authToken = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        ...options.headers,
      },
      ...options,
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return { data }
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Unknown error' }
  }
}

// Projects API
export const projectsApi = {
  getAll: async (params?: {
    featured?: boolean
    status?: string
  }): Promise<ApiListResponse<Project>> => {
    const searchParams = new URLSearchParams()
    if (params?.featured) searchParams.set('featured', 'true')
    if (params?.status) searchParams.set('status', params.status)
    
    const url = `${apiEndpoints.projects}${searchParams.toString() ? `?${searchParams}` : ''}`
    return apiCall<Project[]>(url)
  },

  getById: async (id: string): Promise<ApiResponse<Project>> => {
    return apiCall<Project>(`${apiEndpoints.projects}/${id}`)
  },

  create: async (project: CreateProject): Promise<ApiResponse<Project>> => {
    return apiCall<Project>(apiEndpoints.projects, {
      method: 'POST',
      body: JSON.stringify(project),
    })
  },

  update: async (id: string, project: UpdateProject): Promise<ApiResponse<Project>> => {
    return apiCall<Project>(`${apiEndpoints.projects}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    })
  },

  delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>(`${apiEndpoints.projects}/${id}`, {
      method: 'DELETE',
    })
  },
}

// Press Kit API
export const pressKitApi = {
  getAll: async (params?: {
    featured?: boolean
    category?: string
  }): Promise<ApiListResponse<PressKitItem>> => {
    const searchParams = new URLSearchParams()
    if (params?.featured) searchParams.set('featured', 'true')
    if (params?.category) searchParams.set('category', params.category)
    
    const url = `${apiEndpoints.pressKit}${searchParams.toString() ? `?${searchParams}` : ''}`
    return apiCall<PressKitItem[]>(url)
  },

  getById: async (id: string): Promise<ApiResponse<PressKitItem>> => {
    return apiCall<PressKitItem>(`${apiEndpoints.pressKit}/${id}`)
  },

  create: async (item: CreatePressKitItem): Promise<ApiResponse<PressKitItem>> => {
    return apiCall<PressKitItem>(apiEndpoints.pressKit, {
      method: 'POST',
      body: JSON.stringify(item),
    })
  },

  update: async (id: string, item: UpdatePressKitItem): Promise<ApiResponse<PressKitItem>> => {
    return apiCall<PressKitItem>(`${apiEndpoints.pressKit}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    })
  },

  delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>(`${apiEndpoints.pressKit}/${id}`, {
      method: 'DELETE',
    })
  },
}

// Careers API
export const careersApi = {
  getAll: async (params?: {
    active?: boolean
    type?: string
    department?: string
  }): Promise<ApiListResponse<Career>> => {
    const searchParams = new URLSearchParams()
    if (params?.active === false) searchParams.set('active', 'false')
    if (params?.type) searchParams.set('type', params.type)
    if (params?.department) searchParams.set('department', params.department)
    
    const url = `${apiEndpoints.careers}${searchParams.toString() ? `?${searchParams}` : ''}`
    return apiCall<Career[]>(url)
  },

  getById: async (id: string): Promise<ApiResponse<Career>> => {
    return apiCall<Career>(`${apiEndpoints.careers}/${id}`)
  },

  create: async (career: CreateCareer): Promise<ApiResponse<Career>> => {
    return apiCall<Career>(apiEndpoints.careers, {
      method: 'POST',
      body: JSON.stringify(career),
    })
  },

  update: async (id: string, career: UpdateCareer): Promise<ApiResponse<Career>> => {
    return apiCall<Career>(`${apiEndpoints.careers}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(career),
    })
  },

  delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>(`${apiEndpoints.careers}/${id}`, {
      method: 'DELETE',
    })
  },
}

// Blog API
export const blogApi = {
  getAll: async (params?: {
    status?: string
    featured?: boolean
    tag?: string
    limit?: number
  }): Promise<ApiListResponse<BlogPost>> => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.featured) searchParams.set('featured', 'true')
    if (params?.tag) searchParams.set('tag', params.tag)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    
    const url = `${apiEndpoints.blog}${searchParams.toString() ? `?${searchParams}` : ''}`
    return apiCall<BlogPost[]>(url)
  },

  getById: async (id: string): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>(`${apiEndpoints.blog}/${id}`)
  },

  getBySlug: async (slug: string): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>(`${apiEndpoints.blog}/${slug}`)
  },

  create: async (post: CreateBlogPost): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>(apiEndpoints.blog, {
      method: 'POST',
      body: JSON.stringify(post),
    })
  },

  update: async (id: string, post: UpdateBlogPost): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>(`${apiEndpoints.blog}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(post),
    })
  },

  delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>(`${apiEndpoints.blog}/${id}`, {
      method: 'DELETE',
    })
  },
}
